import { useEffect, useState } from 'react';
import i18n from 'i18n';
import config from 'config';

import { Modal } from 'components/common/Modal/Modal';

// The web app version is baked into the bundle at build time as
// `REACT_APP_VERSION` (exposed via `config.version`) and also written to a
// static `/version` file — CI derives both from the same git tag (see
// scripts/switch-live-env.sh writing `.env.production.local` and
// BuildJenkinsfile `echo $APP_VERSION > public/version`). `/version` is served
// by nginx with `no-store`, so polling it and comparing against the version
// this bundle was built with tells us when a newer version has been deployed,
// and we prompt the user to refresh.
const LOADED_VERSION = config.version as string | undefined;
const VERSION_URL = '/version';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const fetchVersion = async (): Promise<string | undefined> => {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) {
      return undefined;
    }
    const version = (await response.text()).trim();
    // In local dev `/version` doesn't exist and Vite's SPA fallback serves
    // index.html with a 200, so guard against anything that isn't a plain,
    // single-line version string (HTML contains '<' / newlines).
    if (!version || version.includes('<') || /\s/.test(version)) {
      return undefined;
    }
    return version;
  } catch {
    // Network hiccups are ignored; we retry on the next poll.
    return undefined;
  }
};

export const VersionChecker: React.FC = () => {
  const [newVersion, setNewVersion] = useState<string>();

  useEffect(() => {
    // Without a known build version there is nothing reliable to compare
    // against (e.g. local dev where `/version` isn't produced), so skip.
    if (!LOADED_VERSION) {
      return;
    }

    let active = true;

    const check = async () => {
      const current = await fetchVersion();
      if (!active || !current) {
        return;
      }
      if (current !== LOADED_VERSION) {
        setNewVersion(current);
      }
    };

    check();
    const timer = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  if (!newVersion) {
    return null;
  }

  return (
    <Modal
      title={i18n.t('versionChecker.title', { version: newVersion })}
      enableCloseBtn={false}
      backdrop='static'
      primaryButton={{
        title: i18n.t('versionChecker.reload'),
        callback: () => window.location.reload()
      }}
    >
      {i18n.t('versionChecker.message')}
    </Modal>
  );
};
